
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingActions from '../../components/feature/FloatingActions';
import AnimatedSection from '../../components/base/AnimatedSection';

const QuotePage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    services: [] as string[],
    projectDetails: '',
    files: '',
    deliveryOption: '',
    deliveryDate: '',
    name: '',
    email: '',
    phone: '',
    company: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const steps = [
    { number: 1, title: 'Service Selection' },
    { number: 2, title: 'Project Details' },
    { number: 3, title: 'Delivery Options' },
    { number: 4, title: 'Contact Information' },
  ];

  const services = [
    { id: 'dental-crowns', name: 'Dental Crowns & Bridges', icon: 'ri-tooth-line' },
    { id: 'dental-implants', name: 'Dental Implants', icon: 'ri-tooth-line' },
    { id: 'blood-testing', name: 'Blood Testing', icon: 'ri-test-tube-line' },
    { id: 'pathology', name: 'Pathology Services', icon: 'ri-microscope-line' },
    { id: 'genetic-testing', name: 'Genetic Testing', icon: 'ri-dna-line' },
    { id: 'microbiology', name: 'Microbiology Testing', icon: 'ri-virus-line' },
    { id: 'research', name: 'Research Services', icon: 'ri-flask-line' },
    { id: 'quality-control', name: 'Quality Control', icon: 'ri-shield-check-line' },
  ];

  const handleServiceToggle = (serviceId: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId],
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('services', formData.services.join(', '));
      formBody.append('projectDetails', formData.projectDetails);
      formBody.append('files', formData.files);
      formBody.append('deliveryOption', formData.deliveryOption);
      formBody.append('deliveryDate', formData.deliveryDate);
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('company', formData.company);

      const response = await fetch('https://readdy.ai/api/form/d4m4tln7s74mkk8pdu9g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FloatingActions />

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/0cf90334eb010c7f6a7eaa8bd11f49e3.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 to-royal-900/70"></div>
        </div>

        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-5xl lg:text-6xl font-display font-bold mb-4">Request a Quote</h1>
          <p className="text-xl mb-6 max-w-2xl mx-auto">
            Get a customized quote for your laboratory needs
          </p>
          <div className="flex items-center justify-center space-x-2 text-white/80">
            <Link to="/" className="hover:text-white transition-colors duration-300">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>Request Quote</span>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          {/* Progress Bar */}
          <div className="mb-16">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.number} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-12 h-12 flex items-center justify-center rounded-full font-bold transition-all duration-300 ${
                        currentStep >= step.number
                          ? 'bg-primary-500 text-white'
                          : currentStep > step.number
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {currentStep > step.number ? (
                        <i className="ri-check-line text-xl"></i>
                      ) : (
                        step.number
                      )}
                    </div>
                    <span
                      className={`mt-2 text-sm font-semibold text-center ${
                        currentStep >= step.number ? 'text-primary-600' : 'text-gray-500'
                      }`}
                    >
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`h-1 flex-1 mx-4 transition-all duration-300 ${
                        currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                    ></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <AnimatedSection>
            <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
              <form onSubmit={handleSubmit} data-readdy-form>
                {/* Step 1: Service Selection */}
                {currentStep === 1 && (
                  <div>
                    <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">
                      Select Services
                    </h2>
                    <p className="text-gray-600 mb-8">
                      Choose the services you're interested in (select multiple if needed)
                    </p>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {services.map((service) => (
                        <button
                          key={service.id}
                          type="button"
                          onClick={() => handleServiceToggle(service.id)}
                          className={`p-6 rounded-2xl border-2 transition-all duration-300 text-left cursor-pointer ${
                            formData.services.includes(service.id)
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-primary-300'
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            <div
                              className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${
                                formData.services.includes(service.id)
                                  ? 'bg-primary-500 text-white'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              <i className={`${service.icon} text-xl`}></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900">{service.name}</h3>
                            </div>
                            {formData.services.includes(service.id) && (
                              <i className="ri-checkbox-circle-fill text-primary-500 text-xl"></i>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 2: Project Details */}
                {currentStep === 2 && (
                  <div>
                    <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">
                      Project Details
                    </h2>
                    <p className="text-gray-600 mb-8">
                      Tell us more about your project requirements
                    </p>

                    <div className="space-y-6">
                      <div>
                        <label htmlFor="projectDetails" className="block text-sm font-semibold text-gray-700 mb-2">
                          Project Description *
                        </label>
                        <textarea
                          id="projectDetails"
                          name="projectDetails"
                          value={formData.projectDetails}
                          onChange={handleChange}
                          required
                          maxLength={500}
                          rows={6}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 resize-none text-sm"
                          placeholder="Describe your project requirements, specifications, and any special considerations..."
                        ></textarea>
                        <div className="text-right text-xs text-gray-500 mt-1">
                          {formData.projectDetails.length}/500 characters
                        </div>
                      </div>

                      <div>
                        <label htmlFor="files" className="block text-sm font-semibold text-gray-700 mb-2">
                          File Upload Information
                        </label>
                        <input
                          type="text"
                          id="files"
                          name="files"
                          value={formData.files}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm"
                          placeholder="Describe any files or documents you'll provide"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Note: You can send files via email after submitting this form
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Delivery Options */}
                {currentStep === 3 && (
                  <div>
                    <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">
                      Delivery Options
                    </h2>
                    <p className="text-gray-600 mb-8">
                      Choose your preferred delivery method and timeline
                    </p>

                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-4">
                          Delivery Method *
                        </label>
                        <div className="space-y-3">
                          {[
                            { value: 'standard', label: 'Standard (3-5 business days)', icon: 'ri-truck-line' },
                            { value: 'express', label: 'Express (1-2 business days)', icon: 'ri-rocket-line' },
                            { value: 'same-day', label: 'Same Day (Emergency)', icon: 'ri-flashlight-line' },
                            { value: 'pickup', label: 'In-Person Pickup', icon: 'ri-store-line' },
                          ].map((option) => (
                            <label
                              key={option.value}
                              className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                                formData.deliveryOption === option.value
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-gray-200 hover:border-primary-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name="deliveryOption"
                                value={option.value}
                                checked={formData.deliveryOption === option.value}
                                onChange={handleChange}
                                required
                                className="w-5 h-5 text-primary-500 cursor-pointer"
                              />
                              <div className="ml-4 flex items-center flex-1">
                                <div
                                  className={`w-10 h-10 flex items-center justify-center rounded-lg mr-3 ${
                                    formData.deliveryOption === option.value
                                      ? 'bg-primary-500 text-white'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  <i className={`${option.icon} text-xl`}></i>
                                </div>
                                <span className="font-semibold text-gray-900">{option.label}</span>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label htmlFor="deliveryDate" className="block text-sm font-semibold text-gray-700 mb-2">
                          Preferred Delivery Date
                        </label>
                        <input
                          type="date"
                          id="deliveryDate"
                          name="deliveryDate"
                          value={formData.deliveryDate}
                          onChange={handleChange}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Contact Information */}
                {currentStep === 4 && (
                  <div>
                    <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">
                      Contact Information
                    </h2>
                    <p className="text-gray-600 mb-8">
                      Provide your contact details so we can send you the quote
                    </p>

                    <div className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm"
                            placeholder="John Doe"
                          />
                        </div>

                        <div>
                          <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                            Company/Organization
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm"
                            placeholder="Your Company"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm"
                            placeholder="john@example.com"
                          />
                        </div>

                        <div>
                          <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                            Phone Number *
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm"
                            placeholder="+1 (234) 567-890"
                          />
                        </div>
                      </div>

                      {/* Summary */}
                      <div className="mt-8 p-6 bg-gray-50 rounded-2xl">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Quote Summary</h3>
                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Selected Services:</span>
                            <span className="font-semibold text-gray-900">{formData.services.length} service(s)</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Delivery Method:</span>
                            <span className="font-semibold text-gray-900 capitalize">
                              {formData.deliveryOption.replace('-', ' ')}
                            </span>
                          </div>
                          {formData.deliveryDate && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Preferred Date:</span>
                              <span className="font-semibold text-gray-900">{formData.deliveryDate}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {submitStatus === 'success' && (
                      <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center space-x-2 text-green-700">
                          <i className="ri-checkbox-circle-line text-xl"></i>
                          <span className="font-semibold">Quote request submitted successfully! We'll contact you within 24 hours.</span>
                        </div>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center space-x-2 text-red-700">
                          <i className="ri-error-warning-line text-xl"></i>
                          <span className="font-semibold">Something went wrong. Please try again.</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between mt-12 pt-8 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={currentStep === 1}
                    className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line mr-2"></i>
                    Back
                  </button>

                  {currentStep < 4 ? (
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={
                        (currentStep === 1 && formData.services.length === 0) ||
                        (currentStep === 2 && !formData.projectDetails) ||
                        (currentStep === 3 && !formData.deliveryOption)
                      }
                      className="px-8 py-3 bg-gradient-to-r from-primary-500 to-royal-600 text-white rounded-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      Next
                      <i className="ri-arrow-right-line ml-2"></i>
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSubmitting || submitStatus === 'success'}
                      className="px-8 py-3 bg-gradient-to-r from-primary-500 to-royal-600 text-white rounded-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSubmitting ? 'Submitting...' : submitStatus === 'success' ? 'Submitted!' : 'Submit Request'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QuotePage;
